import { Form, Formik } from 'formik';
import moment from 'moment';
import React, { useContext, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { validateChiDinhDV } from 'src/app/modules/phan-he-kham-benh/services/ChiDinhDVService';
import { ServiceSplitHelper } from 'src/app/modules/phan-he-kham-benh/utils/ServiceSplitHelper';
import * as Yup from "yup";
import { AppContext } from '../../../appContext/AppContext';
import { KEY_LOCALSTORAGE } from '../../../auth/core/_consts';
import { ConfirmDialog } from '../../../component/ConfirmDialog';
import { usePhanHeNoiTruContext } from '../../../phan-he-noi-tru/PhanHeNoiTruContext';
import { removeEventEnter } from '../../../utils/AppFunction';
import { APP_TIMESTAMP_FORMAT, CODE, MESSAGE } from '../../../utils/Constant';
import { formatDateDTO, formatDateTime, getErrorMessage } from '../../../utils/FormatUtils';
import { localStorageItem } from '../../../utils/LocalStorage';
import { usePhanHeDieuTriNoiTruContext } from '../../PhanHeDieuTriContext';
import { fetchChiDinhDichVu, updatePhieuChiDinh } from '../../services/DieuTriServices';
import FormChiDinhDV from './FormChiDinhDV';

type Props = {
    phieuDieuTri?: any
    handleClose: () => void
    orderGroupId?: string
}

const ChiDinhDichVu = (props: Props) => {
    const { handleClose, phieuDieuTri, orderGroupId } = props;
    const intl = useIntl();
    const { setIsLoading } = useContext(AppContext);
    const { benhNhanDieuTri } = usePhanHeDieuTriNoiTruContext();
    const { statusChangedEventFunc } = usePhanHeNoiTruContext();

    const formikRef = useRef<any>(null);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [shouldOpenConfirmDialog, setShouldOpenConfirmDialog] = useState<boolean>(false);
    const [validateMessage, setValidateMessage] = useState<string>("");
    const [orderGroupIds, setOrderGroupIds] = useState<string[]>([]);
    const [openPrintDialog, setOpenPrintDialog] = useState<boolean>(false);
    const [sessionCode, setSessionCode] = useState<string>("");
    const isSaveAndPrint = useRef<boolean>(false);
    const userMetaData = localStorageItem.get(KEY_LOCALSTORAGE.USER_METADATA);

    const categoriesRef = useRef<{
        configPriceObject: any[];
        objectTypes: any[];
    }>({
        configPriceObject: [],
        objectTypes: [],
    });

    let currentUser = localStorageItem.get(KEY_LOCALSTORAGE.CURRENT_USER);

    const initialValues = {
        prescriptionDate: formatDateDTO(phieuDieuTri?.prescriptionDate || new Date()),
        treatmentSheet: phieuDieuTri || null,
        orders: [],
        encounterDiagnosis: {
            diagnosisCoded: benhNhanDieuTri?.icd10ProvisionalDiagnosisCode || null,
            diagnosisCodedName: benhNhanDieuTri?.icd10ProvisionalDiagnosisName || null,
            diagnosisNonCoded:  null,
        },
    };

    const validationSchema = Yup.object().shape({
        encounterDiagnosis: Yup.object().shape({
            diagnosisCoded: Yup.string().nullable().test({
                name: 'diagnosisCoded',
                message: MESSAGE.REQUIRED,
                test: (value, { parent }) => {
                    if (!value && !parent?.diagnosisNonCoded) return false;
                    return true;
                }
            }),
            diagnosisNonCoded: Yup.string().nullable().test({
                name: 'diagnosisCoded',
                message: MESSAGE.REQUIRED,
                test: (value, { parent }) => {
                    if (!value && !parent?.diagnosisCoded) return false;
                    return true;
                }
            }),
        }),
        treatmentSheet: Yup.object().nullable().required(intl.formatMessage({ id: "VALIDATION.REQUIRE" })),
    });

    const handleConvertTreeData = (data: any[]) => {
        let convertedData: any[] = data.map((item, index) => {
            return {
                ...item,
                code: item?.code || item?.name,
                name: item?.name,
                ...(item?.terms &&
                    item?.terms?.length && {
                    filter: handleConvertTreeData(item?.terms || []),
                }),
            };
        });
        return convertedData;
    };

    const handleSubmit = async (values: any) => {
        if (!(values?.orders ?? values?.svcItems ?? []).length) {
            toast.warn("Chưa có dịch vụ được chỉ định cho phiếu này");
            return;
        }
        let submitData, _ordersDto;
        if (values?.allowDisableRules) {
            submitData = values;
            _ordersDto = values?.svcItems;
        } else {
            let encounterDiagnosisDto = {
                ...values?.encounterDiagnosis,
            };
            // Step 1: Expand rows that have _splitItems OR quantity > 1
            const expandedOrders = (values?.orders || []).flatMap((order: any) => {
                // Priority 1: Row đã edit popup → Use split items
                if (order._hasSplitItems && order._splitItems) {
                    console.log('Expanding row with _splitItems:', {
                        code: order.code,
                        splitItemsCount: order._splitItems.length,
                    });
                    return order._splitItems;
                }

                // Priority 2: Row có quantity > 1 NHƯNG chưa edit popup
                // → Tạo split items on-the-fly
                const quantity = order?.attributes?.svcQuantity || 1;
                if (quantity > 1) {
                    console.log('Auto-expanding row with quantity > 1:', {
                        code: order.code,
                        quantity,
                    });
                    const splitItems = ServiceSplitHelper.createSplitItems(order, quantity);
                    return splitItems;
                }

                // Priority 3: Regular row (quantity = 1) → Keep as is
                return [order];
            });
            // Step 2: Map to svcItems (following old code pattern)
            let svcItems = expandedOrders?.map((item: any) => {
                // Extract id BEFORE cleaning (cleanForBackend removes id)
                const { id, ...itemValues } = item ?? {};

                // Clean split metadata from itemValues
                const cleanedItem = ServiceSplitHelper.cleanForBackend([itemValues])[0];

                // DTO structure matches backend expectations
                const dto = {
                    ...cleanedItem,
                    // termId: Map service id to termId (required by backend)
                    termId: id,
                    fulfillDeptCode: item?.fulfillDeptCode,
                    fulfillDeptName: item?.fulfillDeptName,
                    // Map visitObjectType to flat fields
                    objectTypeId: item?.visitObjectType?.id,
                    objectTypeCode: item?.visitObjectType?.code,
                    objectTypeName: item?.visitObjectType?.name,
                    // Ensure boolean
                    isOwnExpenses: Boolean(item?.isOwnExpenses),
                };

                return dto;
            });
            _ordersDto = svcItems;
            let autoPrescriptions = values?.autoPrescriptions?.map((item: any) => ({
                prescriptionDate: item?.value,
                treatmentSheetId: item?.treatmentSheetId,
            })) || [];
            submitData = {
                ...encounterDiagnosisDto,
                treatmentSheetId: values?.treatmentSheet?.id || phieuDieuTri?.id,
                autoPrescriptions,
                prescriptionPerson: currentUser?.name,
                prescriptionDate:
                    formatDateTime(values?.prescriptionDate, APP_TIMESTAMP_FORMAT) ||
                    formatDateDTO(new Date()),
                svcItems,
                orderGroupId: orderGroupId,
                patientId: values?.patientId,
                encounterId: values?.encounterId,
                pid: userMetaData?.personId,
            };
        };

        let validateData = {
            ordersDto: _ordersDto,
            visitId: benhNhanDieuTri?.visitId,
            indicationTime: formatDateTime(values?.prescriptionDate, APP_TIMESTAMP_FORMAT) || formatDateDTO(new Date()),
        };

        try {
            setIsLoading(true);

            if (!validateMessage) {
                let { data: validateResponse } = await validateChiDinhDV(validateData);
                if (validateResponse?.code === CODE.SUCCESS) {
                    setValidateMessage("");
                } else {
                    let errorMessage = getErrorMessage(validateResponse);
                    const errorMessageFormatted = Array.isArray(errorMessage)
                        ? errorMessage.map((error: string) => <div key={error}>{error}</div>)
                        : errorMessage;
                    if (validateResponse?.code === CODE.ERROR_CODE_400 || Array.isArray(errorMessage)) {
                        setValidateMessage(errorMessageFormatted);
                        setShouldOpenConfirmDialog(true);
                    } else {
                        toast.warning(errorMessage);
                        statusChangedEventFunc?.();
                        handleClose();
                    }
                    return;
                }
            }
           
            let { data } = orderGroupId ? await updatePhieuChiDinh(submitData, handleSubmit) : await fetchChiDinhDichVu(submitData, handleSubmit);
            if (CODE.SUCCESS === data?.code) {
                setOrderGroupIds(data?.data?.orderGroupIds || data?.data);
                setSessionCode(data?.data?.sessionCode);
                statusChangedEventFunc?.();
                toast.success("Chỉ định dịch vụ thành công");
                isSaveAndPrint.current && setOpenPrintDialog(true);
                !isSaveAndPrint.current && handleClose();
            } else {
                toast.error(getErrorMessage(data));
            }
        } catch (error) {
            console.error(error);
            toast.warning("Xảy ra lỗi, vui lòng thử lại!");
        } finally {
            setIsLoading(false);
        }
    };

    const getObjectPriceFieldConfig = (objectTypeValue: any) => {
      const priceConfigField = categoriesRef.current.configPriceObject.find((config) => {
        return config.termObjectTypeCode === objectTypeValue
      })?.termAttributeField;

      if (!priceConfigField) return null;

      // eslint-disable-next-line no-new-func
      const calculatePriceFn = new Function('moment', `${priceConfigField}`);
      return calculatePriceFn(moment);
    };

    return (
        <Modal
            className="page-full"
            fullscreen
            dialogClassName="h-modal"
            show
            animation={false}
            backdropClassName="spaces top-50"
        >
            <Formik<any>
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnChange={true}
                onSubmit={handleSubmit}
                innerRef={formikRef}
            >
                {({ submitForm }) => (
                    <Form
                        id="form-thuoc"
                        className="spaces h-calc-vh-50"
                        onKeyDown={removeEventEnter}
                    >
                        <FormChiDinhDV 
                            {...props}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                            orderGroupIds={orderGroupIds}
                            isSaveAndPrint={isSaveAndPrint}
                            openPrintDialog={openPrintDialog}
                            setOpenPrintDialog={setOpenPrintDialog}
                            sessionCode={sessionCode}
                        />

                        <ConfirmDialog
                            className="z-index-1060"
                            show={shouldOpenConfirmDialog}
                            title="Thông báo"
                            message={validateMessage}
                            yes="Có"
                            close="Không"
                            onCloseClick={() => {
                                setValidateMessage("");
                                setShouldOpenConfirmDialog(false);
                            }}
                            onYesClick={() => {
                                submitForm();
                                setShouldOpenConfirmDialog(false);
                            }}
                        />
                    </Form>
                )}
            </Formik>
        </Modal>
    );
}

export default ChiDinhDichVu;
